/* eslint-disable react/no-unstable-nested-components */
import React, { useContext, useEffect, useState } from "react";

import classNames from "classnames";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import dynamic from "next/dynamic";
import Image from "next/legacy/image";
import { useSearchParams, useParams } from "../../hooks/useNavigation";
import PropTypes from "prop-types";
import { lighten } from "polished";
import { isMobile } from "react-device-detect";
import styled from "styled-components";
import DescriptiveItem from "../SingleJob/DescriptiveItem";
import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import breakpoints from "../../constants";
import {
  useAnalytics,
  getUTMFromParams,
} from "../../helpers/AnalyticsTrackingContext";
import { manageSessionId } from "../../helpers/tracking";
import { hoursTypeMap } from "../../helpers/vacancyHelpers";
import Button from "../Button/Button";
import JobAlert from "../JobAlert/JobAlert";
import VacancyHead from "../VacancyHead/VacancyHead";
import ApplicationFormModal from "./ApplicationFormModal";
import ExpiredVacancyAlert from "./ExpiredVacancyAlert";
import ImageGallery from "./ImageGallery";
import ReferAFriend from "./CardListWithVacancyView/ReferSomeone";
import VacancyNotFound from "./VacancyNotFound";
import VacancyLoader from "./VacnacyLoader";

const DynamicTFChatBot = dynamic(
  () =>
    import("@e4s-developers/tf-careersite-chatbot").then((mod) => {
      return mod.TFChatBot;
    }),
  {
    ssr: false,
  }
);

const StyledBackButton = styled.div`
  cursor: pointer;
  position: absolute;
  top: -3rem;
  left: 0.25rem;
  display: flex;
  .back-img {
    height: 15px;
    width: 15px;
    stroke-width: 0.5;
    rotate: 90deg;
    z-index: 1;
    margin: auto;
  }
  .back-text {
    margin-left: 0.5rem;
  }
`;

const StyledReturnButton = styled.div`
  cursor: pointer;
  display: flex;
  width: fit-content;
  margin-top: 4.5rem;
  margin-bottom: 2.5rem;

  .back-img {
    height: 12px;
    width: 12px;
    stroke-width: 0.5;
    rotate: 90deg;
    z-index: 1;
    margin: auto;
    margin-bottom: 0.38rem;
  }

  .back-text {
    margin-left: 0.5rem;
  }
`;

const StyledHeadWrapper = styled.div`
  position: relative;
  flex-wrap: wrap;
  @media (min-width: ${breakpoints.DESKTOP}) {
    flex-wrap: nowrap;
  }
`;

const ImageHeader = styled.div`
  background: #18191c;
  width: 100%;
  left: 0;
  position: absolute;
  top: 0;
`;

const StyledButtonWrapper = styled.div`
  position: relative;
  flex-wrap: wrap;
  @media (min-width: ${breakpoints.DESKTOP}) {
    flex-wrap: nowrap;
  }
`;
const StyledHeadContainer = styled.div`
  padding-top: ${({ imageHeader }) =>
    imageHeader ? "2rem !important" : "0.5rem !important"};
`;

const StyledBackgroundImage = styled.div`
  background-image: ${({ url }) => (url ? `url(${url})` : "")};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 60vh;
  z-index: -1;
`;

const StyledCenterCardContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 7rem;
  padding-bottom: 2rem;

  .center-card {
    position: relative;
    background: grey;
    border: 1px solid black;
    padding-left: 4rem;
    padding-right: 4rem;
    padding-top: 2rem;
    padding-bottom: 2rem;
    border-radius: 0.5rem;
    color: white;
    margin-left: 2rem;
    margin-bottom: 2rem;
    margin-right: 2rem;
    max-width: 1250px;
  }
`;
const StickyTopBar = styled.div`
  position: -webkit-sticky; /* For Safari */
  position: sticky;
  height: 0;
  top: 0;
  right: 0;
  z-index: 10;

  width: fit-content;
  margin-left: auto;
`;

const StyledSidebar = styled.div`
  position: relative;
  z-index: 20;

  @media (min-width: ${breakpoints.DESKTOP}) {
    position: -webkit-sticky;
    position: sticky;
    top: 2rem;
    align-self: flex-start;
    max-height: calc(100vh - 4rem);
    overflow-y: auto;
  }
`;

const StyledButton = styled(Button)`
  color: ${({ buttonTextColour }) => buttonTextColour || "inherit"};
  background-color: ${({ buttonColour }) => buttonColour || "inherit"};
  border: ${({ buttonColour }) =>
    buttonColour ? `1px solid ${lighten(buttonColour)}` : "inherit"};
  width: ${({ buttonWidth }) => buttonWidth || "200px"};

  &:disabled {
    background-color: ${({ buttonColour }) =>
      buttonColour ? lighten(0.3, buttonColour) : "inherit"};
    border: ${({ buttonColour }) =>
      buttonColour ? `1px solid ${lighten(buttonColour)}` : "inherit"};
  }

  &:hover,
  &:active,
  &:focus {
    background-color: ${({ buttonHoverColour }) =>
      buttonHoverColour || "inherit"};
    border: ${({ buttonHoverColour }) =>
      buttonHoverColour ? `1px solid ${buttonHoverColour}` : "inherit"};
  }
`;

const ConditionalWrapper = ({ condition, wrapper, children }) =>
  condition ? wrapper(children) : children;

function Vacancy(props) {
  const {
    applyButtonText,
    applyButtonPosition,
    descriptive,
    modalMode,
    singleJobView,
    showHead,
    centerCard,
    imageHeader,
    thumbnailUrl,
    hasCategoryCard,
    largeHeader,
  } = props;
  const { sendTrackingEvent } = useAnalytics();
  const [showInteractiveApp, setShowInteractiveApp] = useState(false);
  const params = useParams();
  const searchParams = useSearchParams();
  const { trackingStore } = useStores();
  const search = Object.fromEntries(searchParams.entries());
  const combindedParms = { ...params, ...search };
  const { refererQueryString } = trackingStore;
  const utm = getUTMFromParams(refererQueryString);

  const smartApply = combindedParms["smart-apply"];
  const {
    formHeaderBackgroundColor,
    gallery,
    additionalConfig,
    jobAlert,
    applyButtonClassName,
    applyButtonHeight = "3.5rem",
    applyButtonWidth,
    applyButtonTextColour,
    applyButtonBackgroundColour,
    referAFriend,
    applyButtonBackgroundHoverColour,
  } = useContext(ComponentContext);
  const { configStore, modalStore, vacancyStore, rolesStore } = useStores();
  const { config } = configStore;
  const { openModal } = modalStore;
  const { selectedVacancy, isVacancyLoading } = vacancyStore;

  const formHeaderTextColor =
    config?.applicationFormConfig?.textColor || "#FFFFFF";
  const backgroundColor = config?.applicationFormConfig?.backgroundColor;

  const [isVacancyActive, setIsVacancyActive] = useState(true);
  const isInteractiveApply = smartApply === "true";
  const hasInteractiveApplyForm = Boolean(
    selectedVacancy?.applicationDetail?.appId
  );

  useEffect(() => {
    if (!isEmpty(selectedVacancy)) {
      const isVacancyClosed = selectedVacancy.status === "CLOSED";
      setIsVacancyActive(!isVacancyClosed);
    }
  }, [selectedVacancy]);

  useEffect(() => {
    const setHeight = () => {
      if (imageHeader) {
        const contentDiv = document.getElementById("vacancy-header-wrapper");
        const fullWidthDiv = document.getElementById(
          "vacancy-header-background"
        );

        const height = contentDiv.offsetHeight;
        fullWidthDiv.style.height = `${height + 45}px`;
      }
    };

    window.addEventListener("resize", setHeight);
    setHeight();

    return () => {
      window.removeEventListener("resize", setHeight);
    };
  }, []);

  const buttonClassNames = classNames(
    "px-5 text-nowrap text-uppercase",
    {
      "w-100 my-4": isMobile,
    },
    applyButtonClassName
  );

  const applyButton = (
    <Button
      className={buttonClassNames}
      maxHeight={applyButtonHeight || "3.5rem"}
      height={applyButtonHeight || "3.5rem"}
      width={applyButtonWidth}
      buttonTextColour={applyButtonTextColour}
      buttonColour={applyButtonBackgroundColour}
      buttonHoverColour={applyButtonBackgroundHoverColour}
      onClick={() => {
        const jobId = selectedVacancy.vacancyId || selectedVacancy.id;
        sendTrackingEvent("APPLY_BUTTON_CLICK", jobId, {
          vacancyId: jobId,
        });

        openModal({
          content: (
            <ApplicationFormModal
              formHeaderBackgroundColor={formHeaderBackgroundColor}
              thumbnailUrl={thumbnailUrl}
            />
          ),
          fullscreen: isMobile,
        });
      }}
    >
      {applyButtonText}
    </Button>
  );

   const referAFriendButton = (
    <StyledButton
      className={buttonClassNames}
      id="refer-a-friend-modal-button"
      maxHeight={applyButtonHeight}
      height={applyButtonHeight}
      buttonTextColour={applyButtonTextColour}
      buttonColour={applyButtonBackgroundColour}
      buttonWidth={applyButtonWidth}
      buttonHoverColour={applyButtonBackgroundHoverColour}
      onClick={() => {
        const jobId = selectedVacancy?.vacancyId || selectedVacancy?.id;
        sendTrackingEvent("REFER_A_FRIEND_BUTTON_CLICK", jobId, {
          vacancyId: jobId,
        });

        openModal({
          content: (
            <ReferAFriend
              text={referAFriend?.text}
              buttonText={referAFriend?.buttonText}
              includeEmailAddress={referAFriend?.includeEmailAddress}
              includeFirstName={referAFriend?.includeFirstName}
              includeLastName={referAFriend?.includeLastName}
              successText={referAFriend?.successText}
              copyButtonText={referAFriend?.copyButtonText}
              customSiteUrl={referAFriend?.customSiteUrl}
              jobId={jobId}
            />
          ),
          fullscreen: isMobile,
        });
      }}
    >
      Refer Someone
    </StyledButton>
  );

  const interactiveApplyButton = (
    <Button
      className={buttonClassNames}
      id="vacancy-smart-apply-button"
      maxHeight={applyButtonHeight || "3.5rem"}
      height={applyButtonHeight || "3.5rem"}
      width={applyButtonWidth}
      onClick={() => {
        const jobId = selectedVacancy.vacancyId || selectedVacancy.id;
        sendTrackingEvent("SMART_APPLY_BUTTON_CLICK", jobId, {
          vacancyId: jobId,
        });

        setShowInteractiveApp(!showInteractiveApp);
      }}
    >
      <span style={{ alignItems: "center", display: "flex" }}>
        <svg
          className="icon"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          style={{
            height: "24px",
            marginRight: "10px",
            width: "24px",
          }}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Smart Apply
      </span>
    </Button>
  );

  if (isVacancyLoading) {
    return <VacancyLoader />;
  }

  if (isEmpty(selectedVacancy)) {
    return <VacancyNotFound />;
  }

  let type = null;

  if (descriptive) {
    type = "descriptive";
  } else if (hasCategoryCard) {
    type = "categoryCard";
  } else if (centerCard) {
    type = "withIcons";
  }

  const vacancyRole = rolesStore.roles.find(
    (role) => role.roleName === selectedVacancy.category
  );
  const largeHeaderImage = vacancyRole?.thumbnail?.url || gallery?.[0]?.url  ;

  const content = (
    <ConditionalWrapper
      condition={largeHeader}
      wrapper={(children) => (
        <div className="container-xxl d-flex flex-column-reverse justify-content-end justify-content-md-start gap-5 pt-5 flex-md-row h-100">
          {children}
          {additionalConfig?.stickySidebar ? (
          <StyledSidebar className="d-flex flex-column gap-5 sidebar">
            <div className="vacancy-card">
             <DescriptiveItem vacancy={selectedVacancy} applyButton={applyButton}/>
            </div>
            {jobAlert ? (
              <ComponentContext.Provider value={jobAlert}>
                <JobAlert />
              </ComponentContext.Provider>
            ) : null}
          </StyledSidebar>
          ) : <div className="d-flex flex-column gap-5 sidebar">
            <div className="vacancy-card">
              <div className="vacancy-detail">
                <strong>LOCATION: </strong> {selectedVacancy.location?.city}
              </div>
              <div className="vacancy-detail">
                <strong>SALARY: </strong>
                {selectedVacancy?.remuneration?.description}
              </div>
              <div className="vacancy-detail">
                <strong>TYPE: </strong>
                {hoursTypeMap[selectedVacancy?.hoursType]}
              </div>
            </div>
            {jobAlert ? (
              <ComponentContext.Provider value={jobAlert}>
                <JobAlert />
              </ComponentContext.Provider>
            ) : null}
            :</div>}
        </div>
      )}
    >
      <StyledHeadContainer
        className="py-4 w-100"
        imageHeader={imageHeader}
        title={selectedVacancy?.title}
      >
        {imageHeader && <ImageHeader id="vacancy-header-background" />}
        {showHead && (
          <StyledHeadWrapper
            className="d-flex mb-5 gap-3"
            hasCategoryCard={hasCategoryCard}
            id="vacancy-header-wrapper"
          >
            <VacancyHead
              companyName={selectedVacancy?.companyName}
              hideImage={singleJobView}
              imageHeader={imageHeader}
              imageText={config?.siteName}
              showCompanyName={singleJobView}
              thumbnailUrl={thumbnailUrl}
              type={type}
              vacancy={selectedVacancy}
              category={selectedVacancy?.category}
            />
            {!modalMode && isVacancyActive && applyButton}
            {referAFriend && !modalMode && referAFriendButton}
            {(config?.allowInteractiveApplications || isInteractiveApply) &&
              !modalMode &&
              isVacancyActive &&
              hasInteractiveApplyForm &&
              interactiveApplyButton}
          </StyledHeadWrapper>
        )}
        {modalMode && isVacancyActive && applyButton}
        {referAFriend && modalMode && referAFriendButton}
        <div
          className="mb-5"
          dangerouslySetInnerHTML={{ __html: selectedVacancy.description }}
        />
        <StyledButtonWrapper className="d-flex gap-3">
          {selectedVacancy.description && isVacancyActive && (
            <div
              className={`d-flex justify-content-${applyButtonPosition}`}
              id="vacancy-apply-button"
            >
              {applyButton}
            </div>
          )}
          {referAFriend && referAFriendButton}
          {(config?.allowInteractiveApplications || isInteractiveApply) &&
            isVacancyActive &&
            hasInteractiveApplyForm &&
            interactiveApplyButton}
        </StyledButtonWrapper>
      </StyledHeadContainer>
    </ConditionalWrapper>
  );

  return (
    <div className={`position-relative ${largeHeader ? "w-100" : ""}`}>
      {isVacancyActive && additionalConfig?.hasStickyApplyButton && (
        <StickyTopBar className="sticky-apply-button">
          {applyButton}
        </StickyTopBar>
      )}
      {!isVacancyActive && <ExpiredVacancyAlert />}
      {largeHeader ? (
        <ImageGallery className="job-large-header" url={largeHeaderImage}>
          <div className="container-xxl text-light d-flex flex-column gap-2">
            {additionalConfig?.showReturnButton ? (
              <StyledReturnButton
                id="single-job-back-button"
                onClick={() => window.history.back()}
              >
                <div className="back-img">
                  <Image
                    alt="go back a page"
                    layout="fill"
                    objectFit="contain"
                    src="/chevron-down.svg"
                  />
                </div>
                <div className="back-text">Back to list</div>
              </StyledReturnButton>
            ) : null}
            <div className="job-general-properties d-flex gap-2">
              {!additionalConfig?.showCompanyName ? (
                <span className="company-name">
                  {selectedVacancy?.company?.name}
                </span>
              ) : null}
              {!additionalConfig?.showHoursTitle && selectedVacancy?.hoursType
                ? "\u2022"
                : ""}
              {!additionalConfig?.showHoursTitle ? (
                <span className="hour-label">
                  {hoursTypeMap[selectedVacancy?.hoursType]}
                </span>
              ) : null}
            </div>
            <h1>{selectedVacancy?.title}</h1>
            <div className="d-flex gap-3 flex-column flex-sm-row job-extra-general-properties">
              <span className="location-city">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 16 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="me-3"
                >
                  <path
                    d="M7.72612 0.333008C3.46592 0.333008 0 3.79892 0 8.05909C0 13.3461 6.91412 21.1077 7.2085 21.4355C7.485 21.7435 7.96775 21.743 8.24375 21.4355C8.53812 21.1077 15.4522 13.3461 15.4522 8.05909C15.4522 3.79892 11.9863 0.333008 7.72612 0.333008ZM7.72612 11.9463C5.58271 11.9463 3.83896 10.2025 3.83896 8.05909C3.83896 5.91567 5.58275 4.17192 7.72612 4.17192C9.8695 4.17192 11.6132 5.91572 11.6132 8.05913C11.6132 10.2025 9.8695 11.9463 7.72612 11.9463Z"
                    fill="#23A1B9"
                  />
                </svg>
                {selectedVacancy?.location?.city}
              </span>
              <span className="hours-type d-none">
              <svg xmlns="http://www.w3.org/2000/svg" width="21" height="18" viewBox="0 0 21 18" fill="none"  className="me-3">
                <path d="M2.64336 18H18.3776C19.8199 18 21 16.8434 21 15.4297V6.00518C21 4.58294 19.8199 3.43486 18.3776 3.43486H14.9685C14.8549 3.42629 14.7587 3.34833 14.75 3.24551L14.6189 2.25166C14.4528 0.957929 13.3339 -0.00165712 12.0192 5.64224e-05H8.98601C7.65734 -0.00851131 6.5472 0.959642 6.38112 2.2448L6.25 3.23009C6.23252 3.3329 6.13636 3.41001 6.03147 3.41001H2.62238C1.17133 3.41001 0 4.55809 0 5.98033V15.4048C0 16.8185 1.17133 17.9752 2.62238 17.9752L2.64336 18ZM8.12675 2.46671C8.1792 2.02975 8.54633 1.71275 8.99213 1.71275H12.0236C12.4607 1.70418 12.8365 2.02975 12.889 2.45814L12.9764 3.17783C12.9851 3.28921 12.8977 3.40059 12.7841 3.40916C12.7753 3.40916 12.7666 3.40916 12.7579 3.40916H8.23864C8.11626 3.40059 8.0201 3.30634 8.0201 3.1864C8.0201 3.17783 8.0201 3.16926 8.0201 3.16069L8.12675 2.46671ZM4.61014 8.13854C4.61014 7.7787 4.8986 7.49596 5.26573 7.49596H15.7552C16.1136 7.49596 16.4108 7.7787 16.4108 8.13854V8.56693C16.4108 8.91821 16.1136 9.20951 15.7552 9.20951H5.26573C4.8986 9.20951 4.61014 8.91821 4.61014 8.56693V8.13854ZM4.61014 12.8508C4.61014 12.4909 4.8986 12.2082 5.26573 12.2082H15.7552C16.1136 12.2082 16.4108 12.4909 16.4108 12.8508V13.2792C16.4108 13.6305 16.1136 13.9218 15.7552 13.9218H5.26573C4.8986 13.9218 4.61014 13.6305 4.61014 13.2792V12.8508Z" fill="#23A1B9"/>
              </svg>
              {hoursTypeMap[selectedVacancy?.hoursType]}
              </span>
              <span className="vacancy-details">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="me-3"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.5 2C9.4233 2 7.39323 2.61581 5.66652 3.76957C3.9398 4.92332 2.59399 6.5632 1.79927 8.48182C1.00455 10.4004 0.796614 12.5116 1.20176 14.5484C1.6069 16.5852 2.60693 18.4562 4.07538 19.9246C5.54383 21.3931 7.41475 22.3931 9.45155 22.7982C11.4884 23.2034 13.5996 22.9954 15.5182 22.2007C17.4368 21.406 19.0767 20.0602 20.2304 18.3335C21.3842 16.6068 22 14.5767 22 12.5C22 11.1211 21.7284 9.75574 21.2007 8.48182C20.6731 7.2079 19.8996 6.05039 18.9246 5.07538C17.9496 4.10036 16.7921 3.32694 15.5182 2.79926C14.2443 2.27159 12.8789 2 11.5 2ZM14.3327 17.8102H8.23334C8.08907 17.8102 7.94834 17.7656 7.83039 17.6825C7.71244 17.5995 7.62303 17.482 7.5744 17.3461C7.52578 17.2103 7.5203 17.0628 7.55873 16.9237C7.59715 16.7847 7.6776 16.6609 7.78907 16.5693L10.2232 14.5697C10.0482 14.2985 9.82234 13.949 9.5904 13.5897H8.23334C8.04769 13.5897 7.86964 13.5159 7.73836 13.3846C7.60709 13.2534 7.53334 13.0753 7.53334 12.8897C7.53334 12.704 7.60709 12.526 7.73836 12.3947C7.86964 12.2634 8.04769 12.1897 8.23334 12.1897H8.7C8.602 12.0329 8.51567 11.8943 8.4606 11.8019C8.1773 11.3051 8.02596 10.7441 8.021 10.1723C8.02085 9.44844 8.26328 8.74545 8.70957 8.17559C9.15586 7.60572 9.78026 7.20187 10.483 7.02853C11.1858 6.85519 11.9264 6.92237 12.5864 7.21934C13.2465 7.51631 13.7881 8.02593 14.1245 8.6668C14.2112 8.8311 14.229 9.02309 14.1741 9.20053C14.1192 9.37797 13.996 9.52633 13.8317 9.61296C13.6674 9.6996 13.4754 9.71742 13.298 9.66251C13.1205 9.60759 12.9722 9.48443 12.8855 9.32013C12.6948 8.95619 12.3875 8.6667 12.0128 8.49793C11.6382 8.32917 11.2178 8.29088 10.8188 8.38917C10.4198 8.48747 10.0653 8.71668 9.81189 9.04017C9.5585 9.36366 9.42088 9.76275 9.421 10.1737C9.42557 10.4919 9.50792 10.8041 9.66087 11.0832C9.78547 11.2909 10.0547 11.716 10.3581 12.1911H12.8972C13.0829 12.1911 13.2609 12.2648 13.3922 12.3961C13.5235 12.5274 13.5972 12.7054 13.5972 12.8911C13.5972 13.0767 13.5235 13.2548 13.3922 13.386C13.2609 13.5173 13.0829 13.5911 12.8972 13.5911H11.2578C11.5331 14.0162 11.7338 14.3261 11.7399 14.3349C11.8336 14.4793 11.8709 14.6532 11.8445 14.8233C11.8182 14.9934 11.7301 15.1478 11.5971 15.2571L10.1901 16.413H14.3327C14.5183 16.413 14.6964 16.4867 14.8276 16.618C14.9589 16.7493 15.0327 16.9273 15.0327 17.113C15.0327 17.2986 14.9589 17.4767 14.8276 17.608C14.6964 17.7392 14.5183 17.813 14.3327 17.813V17.8102Z"
                    fill="#23A1B9"
                  />
                </svg>
                {selectedVacancy?.remuneration?.description}
                <br />
              </span>
              <span className="vacancy-category ">
              <svg xmlns="http://www.w3.org/2000/svg" width="5" height="5" viewBox="0 0 5 5" fill="none" className="me-3">
                <circle cx="2.5" cy="2.5" r="2.5" fill="white"/>
              </svg>
                {selectedVacancy?.category}
              </span>
            </div>
            <div className="d-flex justify-content-start pt-5">
              <StyledButtonWrapper className="d-flex gap-3">
                {selectedVacancy.description && isVacancyActive && (
                  <div
                    className="d-flex justify-content-start"
                    id="vacancy-apply-button"
                  >
                    {applyButton}
                  </div>
                )}
                 {referAFriend && referAFriendButton}
                {(config?.allowInteractiveApplications || isInteractiveApply) &&
                  isVacancyActive &&
                  hasInteractiveApplyForm &&
                  interactiveApplyButton}
              </StyledButtonWrapper>
            </div>
          </div>
        </ImageGallery>
      ) : null}
      <ConditionalWrapper
        condition={centerCard}
        wrapper={(children) => (
          <StyledCenterCardContainer
            url={largeHeaderImage}
            className="job-center-card-container"
          >
            <StyledBackgroundImage
              url={largeHeaderImage}
              className="job-center-card-image"
            />
            <div className="center-card">
              <StyledBackButton
                id="single-job-back-button"
                onClick={() => window.history.back()}
              >
                <div className="back-img">
                  <Image
                    alt="go back a page"
                    layout="fill"
                    objectFit="contain"
                    src="/chevron-down.svg"
                  />
                </div>
                <div className="back-text">Back to roles</div>
              </StyledBackButton>
              {children}
            </div>
          </StyledCenterCardContainer>
        )}
      >
        {content}
      </ConditionalWrapper>
      {showInteractiveApp && (
        <DynamicTFChatBot
          formId={selectedVacancy?.applicationDetail?.appId}
          sId={manageSessionId()}
          onClose={() => setShowInteractiveApp(false)}
          show={showInteractiveApp}
          tenant={selectedVacancy.tenant}
          vacancyTitle={selectedVacancy.title || selectedVacancy.jobTitle}
          style={{
            headerBackgroundColour:
              backgroundColor || formHeaderBackgroundColor,
            headerTextColour: formHeaderTextColor,
            headerLogo: selectedVacancy?.company?.logoUrlFull,
          }}
          utm={utm}
        />
      )}
    </div>
  );
}

Vacancy.defaultProps = {
  applyButtonPosition: "end",
  applyButtonText: "Apply",
  descriptive: false,
  hasCategoryCard: false,
  imageHeader: false,
  modalMode: false,
  showHead: true,
  centerCard: false,
  singleJobView: false,
  thumbnailUrl: undefined,
  largeHeader: false,
};

Vacancy.propTypes = {
  applyButtonPosition: PropTypes.string,
  applyButtonText: PropTypes.string,
  descriptive: PropTypes.bool,
  hasCategoryCard: PropTypes.bool,
  imageHeader: PropTypes.bool,
  modalMode: PropTypes.bool,
  centerCard: PropTypes.bool,
  showHead: PropTypes.bool,
  singleJobView: PropTypes.bool,
  thumbnailUrl: PropTypes.string,
  largeHeader: PropTypes.bool,
};

export default observer(Vacancy);
